import { useCallback, useState, useRef } from 'react';
import { getPreviousRank, getNextRank } from '@atlassian/jira-business-rank/src/index.tsx';
import type FetchError from '@atlassian/jira-fetch/src/utils/errors.tsx';
import { performPutRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import {
	useAnalyticsEvents,
	fireOperationalAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import type { GenericVariables } from '../../common/types.tsx';
import { useCacheActions } from '../list-cache/main.tsx';
import type { BaseIssueNode } from '../list-cache/types.tsx';
import type { RankIssueProps } from './types.tsx';
import { getOptimisticRanked, getListViewRelativeIssueInfo } from './utils.tsx';

const performRestCall = (
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	performRequest: (url: string, options?: RequestInit) => Promise<any>,
	url: string,
	issueId: number | string,
	relativeIssueId: number | string,
	setLoading: (arg1: ((arg1: boolean) => boolean) | boolean) => void,
	onError: (arg1: FetchError) => void,
	onSuccess: undefined | (() => void),
) => {
	performRequest(url, {
		body: JSON.stringify({
			issueId: Number(issueId),
			relativeIssueId: Number(relativeIssueId),
		}),
	})
		.then(() => {
			setLoading(false);
			onSuccess && onSuccess();
		})
		.catch((error) => {
			setLoading(false);
			onError && onError(error);
		});
};

export const useRankIssue = <
	IssueType extends {
		issueId: string;
		rank?: string | undefined;
		parentIssueId?: string;
	},
>({
	query,
	variables,
	onError,
	onSuccess,
	onStart,
}: RankIssueProps) => {
	const [loading, setLoading] = useState(false);
	const { getCachedIssueById, updateItemsInCache } = useCacheActions();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const queryRef = useRef(query);
	queryRef.current = query;
	const variablesRef = useRef(variables);
	variablesRef.current = variables;

	const rank = useCallback(
		({
			issueId,
			relativeIssue,
			rankCall,
			skipUpdateCache,
			variablesInput,
		}: {
			issueId: string;
			relativeIssue: IssueType;
			rankCall: string;
			skipUpdateCache?: boolean;
			variablesInput?: GenericVariables;
		}) => {
			setLoading(true);
			onStart && onStart();
			const cacheVariables = variablesInput ?? variablesRef.current;

			const issueToRank = getCachedIssueById({
				issueId,
				query: queryRef.current,
				variables: cacheVariables,
			});
			const relativeIssueRank = relativeIssue.rank;

			if (issueToRank && relativeIssueRank) {
				const optimisticRank =
					rankCall === 'rankBefore'
						? getPreviousRank(relativeIssueRank)
						: getNextRank(relativeIssueRank);

				// Skip updating the rank field in cache if re-rank issue is part of the reparenting operation.
				if (!skipUpdateCache) {
					updateItemsInCache({
						query: queryRef.current,
						variables: cacheVariables,
						issueIds: [issueToRank?.issueId],
						updateItem: () => getOptimisticRanked(issueToRank, optimisticRank),
					});
				}

				performRestCall(
					performPutRequest,
					`/rest/board-api/1/rank/${rankCall}`,
					Number(issueToRank.issueId),
					Number(relativeIssue.issueId),
					setLoading,
					(error: FetchError) => {
						onError(error);
						updateItemsInCache({
							query,
							variables: cacheVariables,
							issueIds: [issueToRank?.issueId],
							updateItem: (existingIssue) => ({
								...existingIssue,
								fields: existingIssue.fields
									?.filter((field) => field?.aliasFieldId !== 'rank')
									.concat(issueToRank.fields?.find((field) => field?.aliasFieldId === 'rank')),
							}),
						});
					},
					() => {
						onSuccess && onSuccess();
						fireOperationalAnalytics(createAnalyticsEvent({}), 'issueRank succeeded');
					},
				);
			} else {
				setLoading(false);
			}
		},
		[
			createAnalyticsEvent,
			getCachedIssueById,
			onError,
			onStart,
			onSuccess,
			query,
			updateItemsInCache,
		],
	);

	const rankListViewIssue = useCallback(
		({
			newIndex,
			oldIndex,
			issues,
			issueToRank: prevIssueToRank,
			variables: variablesInput,
			skipUpdateCache,
			instruction,
			relativeIssueOverride,
		}: {
			newIndex: number;
			oldIndex: number;
			issues: IssueType[];
			issueToRank?: BaseIssueNode;
			variables?: GenericVariables;
			skipUpdateCache?: boolean;
			instruction?: string;
			relativeIssueOverride?: IssueType;
		}) => {
			const rankingIssue = prevIssueToRank ?? issues[oldIndex];
			const { rankCall, relativeIssue } = getListViewRelativeIssueInfo({
				newIndex,
				oldIndex,
				issues,
				instruction,
				relativeIssueOverride,
			});

			if (relativeIssue) {
				rank({
					relativeIssue,
					issueId: rankingIssue.issueId,
					rankCall,
					variablesInput,
					skipUpdateCache,
				});
			}
		},
		[rank],
	);

	return {
		loading,
		rank,
		rankListViewIssue,
	};
};
